import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { App } from './app/app';

// sockjs-client (WebSocket fallback transport) references the Node-style `global` at module load;
// the browser has no `global`, so alias it to `window` before anything imports it.
(window as any).global = (window as any).global || window;

declare var leanbyte: any;

const DEEPLINK_KEY = 'lbDeepLink';
const DEEPLINK_TTL_MS = 5 * 60 * 1000;
const DEEPLINK_EXCLUDED = ['#/login', '#/candidate-login', '#/qr-login', '#/lite-login', '#/l', '#/accessdenied'];

function clearStaleTokensOnEnvironmentSwitch(isMicrosoftLogin: boolean): void {
  const token = localStorage.getItem('authToken');
  if (!token) return;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));

    // KCS-565 Part 2: a candidate (temp user) token is valid on BOTH on-prem and Microsoft-login
    // instances (it's our HS256 token, never an Azure one). It must survive this wipe — otherwise
    // every page load on MSL would destroy an active candidate session. Detect it by its claim.
    if (payload.candidate === true) return;

    const isCloudToken = payload.iss?.includes('login.microsoftonline.com') || payload.iss?.includes('sts.windows.net');

    if ((isMicrosoftLogin && !isCloudToken) || (!isMicrosoftLogin && isCloudToken)) {
      // OnPrem↔Cloud token mismatch → wipe stored tokens.
      console.log(`Environment switch detected (${isMicrosoftLogin ? 'OnPrem → Cloud' : 'Cloud → OnPrem'}). Clearing stored tokens.`);
      const deepLink = sessionStorage.getItem(DEEPLINK_KEY);
      localStorage.clear();
      sessionStorage.clear();
      if (deepLink !== null) { sessionStorage.setItem(DEEPLINK_KEY, deepLink); }
    }
  } catch {
    // Malformed token → clear to be safe
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
  }
}

/**
 * KCS-565 Part 2: printed candidate QR cards encode a PATH url (…/qr-login?token=…), but the app uses
 * hash routing (withHashLocation). On MSL the path also sits outside MsalGuard. Rewrite a /qr-login path
 * hit to the hash route BEFORE MSAL initialises, so already-printed cards land on the candidate login page
 * instead of triggering a Microsoft redirect. New cards are minted in the hash form directly (backend).
 */
function normalizeQrLoginPath(): void {
  if (window.location.pathname === '/qr-login') {
    window.history.replaceState(null, '', '/#/qr-login' + window.location.search);
  }
}

/**
 * KCS-565: the dedicated candidate login is a hash route (#/candidate-login). If someone opens the bare
 * path (a kiosk shortcut / bookmark typed without the #), rewrite it to the hash route BEFORE MSAL init so
 * it lands on the candidate screen instead of triggering a Microsoft redirect / 404. In-app links already
 * produce the hash form.
 */
function normalizeCandidateLoginPath(): void {
  if (window.location.pathname === '/candidate-login') {
    window.history.replaceState(null, '', '/#/candidate-login' + window.location.search);
  }
}

function isRestorableHash(hash: string): boolean {
  if (!hash.startsWith('#/') || hash === '#/') return false;
  if (DEEPLINK_EXCLUDED.some(route => hash === route || hash.startsWith(`${route}?`) || hash.startsWith(`${route}/`))) return false;
  const path = hash.slice(1);
  if (path.startsWith('//')) return false;
  if (/^\/[^/?#]*:/.test(path)) return false;
  return true;
}

function captureDeepLink(): void {
  const hash = window.location.hash;
  if (isRestorableHash(hash)) {
    sessionStorage.setItem(DEEPLINK_KEY, JSON.stringify({ h: hash, t: Date.now() }));
  }
}

function restoreDeepLink(hasAccount: boolean): void {
  const raw = sessionStorage.getItem(DEEPLINK_KEY);
  if (!raw) return;
  if (!hasAccount) return;
  sessionStorage.removeItem(DEEPLINK_KEY);
  try {
    const { h, t } = JSON.parse(raw);
    if (window.location.hash === h) return;
    if (typeof h === 'string' && typeof t === 'number' && Date.now() - t <= DEEPLINK_TTL_MS && isRestorableHash(h)) {
      window.history.replaceState(null, '', window.location.pathname + h);
    }
  } catch {
    /* malformed stash — ignore */
  }
}

/**
 * KCS-565 Part 2: after an Azure sign-out, LoginService.logout sets a one-shot marker and sends the user
 * back to the registered origin. Route them to the public /login hub here (before MSAL init) so the root
 * route's MsalGuard doesn't immediately bounce them back to Microsoft — giving access to candidate login.
 */
function routePostLogoutToLogin(): void {
  if (sessionStorage.getItem('lbPostLogoutToLogin')) {
    sessionStorage.removeItem('lbPostLogoutToLogin');
    if (!window.location.hash.startsWith('#/login')) {
      window.history.replaceState(null, '', '/#/login');
    }
  }
}

async function bootstrap(): Promise<void> {
  const isMicrosoftLogin = typeof leanbyte !== 'undefined' && leanbyte.microsoftLogin === true;

  clearStaleTokensOnEnvironmentSwitch(isMicrosoftLogin);
  routePostLogoutToLogin();
  normalizeQrLoginPath();
  normalizeCandidateLoginPath();

  if (isMicrosoftLogin) {
    captureDeepLink();

    const { createAndInitializeMsal, setMsalInstance } = await import('./app/core/auth/msal-config');
    const pca = await createAndInitializeMsal();
    setMsalInstance(pca);

    // After MSAL processes the redirect, clean up the URL hash.
    // Microsoft returns #code=AUTH_CODE&state=..., which conflicts with
    // Angular's hash routing (#/path). Reset to #/ so the router navigates home.
    if (window.location.hash && !window.location.hash.startsWith('#/')) {
      window.history.replaceState(null, '', window.location.pathname + '#/');
    }

    const hasAccount = pca.getActiveAccount() != null || pca.getAllAccounts().length > 0;
    restoreDeepLink(hasAccount);
  }

  const appRef = await bootstrapApplication(App, appConfig);

  // Bootstrap the MSAL redirect component as a second root component,
  // mirroring the old app's <app-redirect> in index.html.
  if (isMicrosoftLogin) {
    const { AppRedirectComponent } = await import('./app/core/auth/app-redirect.component');
    appRef.bootstrap(AppRedirectComponent);
  }
}

bootstrap().catch(err => console.error(err));
 